/**
 * VirtualTourEmbed — American Landmark override.
 *
 * Adopts the cleaner inline-iframe embed from the Via6 theme (a single
 * tour in a framed 16:9 player with a lightweight selector for
 * properties that publish more than one tour) instead of the inherited
 * Preferred list/modal treatment.
 *
 * Restyled for the American Landmark Preferred look: the section keeps a
 * white background, and the brand accent comes through the primary color
 * — the selector focuses to a primary ring and the active selector pill
 * uses the primary surface with a text-on-primary label.
 */
import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

/**
 * Returns true only if `value` looks like a real URL/path. Upstream
 * feeds sometimes pass literal strings like "false", "null", "none",
 * etc. instead of an empty value — those should be treated as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower === 'false' ||
    lower === 'true' ||
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'n/a'
  ) {
    return false;
  }
  return true;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => isUsableUrl(t.virtual_tour_url)),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white pt-10 md:pt-14 pb-16 md:pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tour selector — only shown when more than one tour exists. */}
        {hasMultiple && (
          <div className="flex flex-wrap gap-2 mb-6">
            {validTours.map((tour, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedIndex(i)}
                className={`vt-tour-pill font-body text-sm font-medium rounded-full px-4 py-2 border transition-colors duration-200 ${
                  i === selectedIndex
                    ? 'bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)] border-[var(--vibe-color-primary)]'
                    : 'vt-tour-pill--inactive bg-white text-[var(--vibe-color-text)] border-[var(--vibe-color-border)]'
                }`}
              >
                {tour.virtual_tour_title || `Tour ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* Tour embed */}
        {activeTour?.virtual_tour_url ? (
          <div
            className="relative w-full rounded-lg overflow-hidden shadow-lg border border-[var(--vibe-color-border)]"
            style={{ paddingBottom: '56.25%' }}
          >
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>

      <style>{`
        .vt-tour-pill:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--vibe-color-primary);
        }
        .vt-tour-pill--inactive:hover {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default VirtualTourEmbed;
